/**
 * Every API boundary in this app is validated by one of these.
 *
 * They live in `shared` so the form that collects a value and the route that
 * stores it cannot drift — a client-side rule that is laxer than the server's
 * produces a confusing rejection, and one that is stricter produces a field
 * nobody can fill in.
 */
import { z } from 'zod';
import { MAX_AMOUNT_CENTS } from './money.js';
import { isPlausibleAccountNumber, isValidRoutingNumber, hasPlausibleRoutingPrefix, } from './routing-number.js';
const trimmed = (max) => z.string().trim().max(max);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional();
export const amountCentsSchema = z
    .number()
    .int('Amount must be a whole number of cents.')
    .positive('Amount must be greater than zero.')
    .max(MAX_AMOUNT_CENTS, 'Amount is implausibly large — check for a misplaced decimal point.');
export const emailSchema = trimmed(320).email('Enter a valid email address.').toLowerCase();
// --- customers -------------------------------------------------------------
export const customerInputSchema = z.object({
    name: trimmed(200).min(1, 'Customer name is required.'),
    company: optionalText(200),
    email: emailSchema,
    phone: optionalText(40),
    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(100),
    state: optionalText(50),
    postalCode: optionalText(20),
    country: trimmed(2).default('US'),
    notes: optionalText(2000),
});
// --- invoices --------------------------------------------------------------
/**
 * An invoice line on a draft request.
 *
 * `storagePath` is filled in by the upload route, not by the client — the
 * client never chooses where a file lands. `parseStatus` records whether the
 * amount came out of the PDF or off the admin's keyboard, which the UI shows
 * as a badge and which matters when a debit is later disputed.
 */
export const invoiceInputSchema = z.object({
    invoiceNumber: trimmed(60).min(1, 'Invoice number is required.'),
    invoiceDate: z.string().date().nullable().optional(),
    dueDate: z.string().date().nullable().optional(),
    amountCents: amountCentsSchema,
    poNumber: optionalText(60),
    description: optionalText(500),
    storagePath: optionalText(500),
    originalFilename: optionalText(255),
    byteSize: z.number().int().nonnegative().nullable().optional(),
    sha256: optionalText(64),
    parseStatus: z.enum(['parsed', 'manual', 'failed']).default('manual'),
    parsedJson: z.unknown().nullable().optional(),
});
// --- payment requests ------------------------------------------------------
export const paymentRequestInputSchema = z.object({
    customerId: z.string().uuid(),
    invoices: z
        .array(invoiceInputSchema)
        .min(1, 'A payment request needs at least one invoice.')
        .max(50, 'That is more invoices than one request should carry.')
        .refine((invoices) => new Set(invoices.map((i) => i.invoiceNumber)).size === invoices.length, 'The same invoice number appears twice.'),
    noteToCustomer: optionalText(2000),
    verificationKind: z.enum(['total', 'zip']).default('total'),
});
// --- the customer-facing flow ---------------------------------------------
export const verifyInputSchema = z.object({
    /** The invoice total (as typed, e.g. "1,234.56") or the billing ZIP. Normalized server-side. */
    value: z.string().trim().min(1).max(40),
});
/**
 * The bank details, or the id of an account already on file.
 *
 * A discriminated union rather than a single object with everything optional,
 * so it is impossible to reach the handler having sent a saved-account id AND
 * a half-filled new account and have the server pick one.
 */
export const bankDetailsSchema = z
    .discriminatedUnion('mode', [
    z.object({
        mode: z.literal('saved'),
        savedAccountId: z.string().uuid(),
    }),
    z.object({
        mode: z.literal('new'),
        bankName: trimmed(120).min(2, 'Enter the name of your bank.'),
        accountHolderName: trimmed(200).min(2, 'Enter the name on the account.'),
        accountType: z.enum(['checking', 'savings']),
        routingNumber: z
            .string()
            .trim()
            .refine(isValidRoutingNumber, 'That routing number fails its checksum — check the digits.')
            .refine(hasPlausibleRoutingPrefix, 'That is not a routing number any US institution has been issued.'),
        accountNumber: z
            .string()
            .trim()
            .refine(isPlausibleAccountNumber, 'Enter the account number — 4 to 17 digits.'),
        accountNumberConfirm: z.string().trim(),
    }),
])
    // The confirm-match check sits on the union rather than on the `new` member:
    // z.discriminatedUnion only accepts plain objects as members, and a member
    // carrying a .refine() is a ZodEffects, which it rejects at the type level.
    .superRefine((value, ctx) => {
    if (value.mode !== 'new')
        return;
    if (value.accountNumber.replace(/\D/g, '') !== value.accountNumberConfirm.replace(/\D/g, '')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'The two account numbers do not match.',
            path: ['accountNumberConfirm'],
        });
    }
});
/**
 * The submission.
 *
 * `authorizationAccepted` is `z.literal(true)` rather than a boolean on
 * purpose: there is no such thing as a submission with the authorization
 * unchecked, so the type system should not permit one to be constructed.
 * `authorizationTextId` echoes back the exact version the customer was shown,
 * and the server rejects it if it is not the version currently in force —
 * which is what stops a stale tab from recording consent to wording that had
 * already been replaced.
 */
export const submitPaymentSchema = z.object({
    bank: bankDetailsSchema,
    authorizationAccepted: z.literal(true, {
        errorMap: () => ({ message: 'You must authorize the debit to submit.' }),
    }),
    authorizationTextId: z.string().uuid(),
    signatureName: trimmed(200).min(2, 'Type your full name to sign.'),
});
// --- admin actions ---------------------------------------------------------
export const bankCsvExportSchema = z.object({
    submissionIds: z.array(z.string().uuid()).min(1).max(500),
    /**
     * One CSV row per invoice (default) keeps each line reconcilable against a
     * Printer's Plan order number. `combined` emits a single row per submission
     * instead, so the customer sees one debit rather than several.
     */
    grouping: z.enum(['per-invoice', 'combined']).default('per-invoice'),
});
export const recordPaymentSchema = z.object({
    note: optionalText(1000),
});
//# sourceMappingURL=schemas.js.map